import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { PerimeterDto } from '../shared/models/perimeter.dto';
import { ToastService } from '../shared/toast/toast.service';
import { BeneficiariesService } from '../shared/services/beneficiaries.service';
import { TRsjPermission } from '../shared/permissions/permissions';
import { RsjPermissionService } from '../shared/permissions/services/rsj-permission.service';
@Injectable({ providedIn: 'root' })
export class BeneficiaryPerimeterGuard {
  public constructor(
    private readonly beneficiariesService: BeneficiariesService,
    private readonly toastService: ToastService,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly rsjPermission: RsjPermissionService,
  ) {
    this.beneficiariesService.accessMode.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((accessMode: string) => (this.accessMode = accessMode));
  }

  private accessMode: string;
  private readonly destroy$: Subject<void> = new Subject();

  public async canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    let hasAccess: boolean = false;

    try {
      if (childRoute.parent.paramMap.has('id') && childRoute.routeConfig.path) {
        const id: number = +childRoute.parent.paramMap.get('id');
        const tab: string = childRoute.routeConfig.path;

        if (childRoute.routeConfig.path.includes('rsj')) {
          const user = await this.authService.getUser();
          let permissions: TRsjPermission[] = ['RSJ_CAN_ACCESS'];
          if (!this.rsjPermission.getPermissions(user).some((value) => permissions?.includes(value))) {
            hasAccess = false;
            this.router.navigate(['beneficiaries']);
          } else {
            hasAccess = true;
          }
        }

        if (id === 0) {
          if (this.authService.isInGroups(['ADMIN', 'TERRITORY', 'STRUCTURE'])) {
            hasAccess = false;
            this.router.navigate(['beneficiaries']);
          } else {
            this.router.navigate(['/']);
          }
        } else if (id > 0) {
          if (this.authService.isInGroups(['ADMIN', 'TERRITORY', 'STRUCTURE', 'RECEPTION', 'DIRECTION', 'DIRECTION+', 'MMIE'])) {
            const perimeter: PerimeterDto = await this.beneficiariesService.getPerimeter(tab, this.accessMode);
            if (perimeter?.canAccess) {
              hasAccess = true;
              this.beneficiariesService.perimeter.setValue(perimeter);
            }
          } else {
            this.router.navigate(['/']);
          }
        }
      } else if (childRoute.parent.routeConfig.path === 'new') {
        const tab: string = childRoute.routeConfig.path;
        switch (tab) {
          case 'personaldata':
            if (this.authService.isInGroups(['ADMIN', 'TERRITORY', 'STRUCTURE', 'RECEPTION'])) {
              hasAccess = true;
              this.beneficiariesService.perimeter.setValue(new PerimeterDto(true, true));
            } else {
              this.router.navigate(['beneficiaries']);
            }
            break;
          case 'complement':
          case 'employment':
          case 'documents':
          case 'interviews':
          case 'contracts':
          case 'stages':
          case 'fse':
          case 'diagnostic':
            if (this.authService.isInGroups(['ADMIN', 'TERRITORY', 'STRUCTURE'])) {
              this.router.navigate(['beneficiaries', 'new']);
            }
            break;
          case 'assessment':
            if (this.authService.isInGroups(['ADMIN', 'TERRITORY', 'STRUCTURE'])) {
              this.router.navigate(['beneficiaries', 'new']);
            }
            break;
          default:
            this.router.navigate(['/']);
            break;
        }
      }
    } catch (error) {
      hasAccess = false;
    }

    if (!hasAccess) {
      this.toastService.show("Vous n'avez pas l'autorisation d'accéder à cette page ou à cette ressource.", 'danger');
    }

    return hasAccess;
  }
}
